/** Курсы: биржевые и заданные вручную.
 *
 * Ручной курс ставился фразой «курс донг 26470» и действовал ГЛОБАЛЬНО — на всех,
 * кто пользуется ботом, — а посмотреть или снять его было нельзя ничем. Поэтому
 * здесь он не просто виден: рядом написано, что он общий.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type Failure, type Rates } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { tap } from "../lib/telegram.ts";
import { Header } from "../components/Header.tsx";
import { Fallback } from "./Fallback.tsx";
import { card, hint } from "../components/ui.tsx";
import { dayLabel } from "../lib/group.ts";

export function RatesScreen({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Rates | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const load = () => {
    setFailure(null);
    api.rates().then(setData)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  const manual = data?.rates.filter((r) => r.manual) ?? [];

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Курсы" onBack={onBack} />

      {!data ? <Skeleton /> : (
        <>
          <section style={{ ...card, padding: "2px var(--s-5)" }}>
            {data.rates.map((rate, i) => (
              <div key={rate.quote}
                   style={{ display: "flex", alignItems: "center", padding: "14px 0",
                            borderBottom: i === data.rates.length - 1
                              ? "none" : "1px solid var(--separator)" }}>
                <div style={{ flexGrow: 1 }}>
                  <div style={{ fontSize: "var(--f-body)" }}>
                    {rate.quote} за 1 USD
                    {rate.manual && (
                      <span style={{ fontSize: "var(--f-micro)", color: "var(--warn)",
                                     background: "color-mix(in srgb, var(--warn) 12%, transparent)",
                                     borderRadius: 5, padding: "2px 6px", marginLeft: 7 }}>
                        вручную
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                                marginTop: 2 }}>
                    {rate.manual ? "задан вручную" : formatUpdated(rate.updated_at)}
                  </div>
                </div>
                <span className="num" style={{ fontSize: "var(--f-body)", fontWeight: 600 }}>
                  {formatMoney(rate.rate, rate.quote)}
                </span>
              </div>
            ))}
          </section>

          {manual.length > 0 && (
            <section style={{ ...card, marginTop: "var(--s-3)" }}>
              <div style={{ fontSize: "var(--f-body)", fontWeight: 600 }}>
                Ручной курс действует на всех
              </div>
              <p style={hint}>
                Он общий для бота, а не личный: его видят все новые записи, чьи бы они ни были.
                {!data.can_edit && " Задаёт и снимает его только владелец бота."}
              </p>
              {data.can_edit && manual.map((rate) => (
                <button key={rate.quote} type="button" aria-disabled={busy || undefined}
                  onClick={() => {
                    if (busy) return;
                    tap();
                    setBusy(true);
                    setTrouble("");
                    api.clearRate(rate.quote).then(setData)
                      .catch(() => setTrouble("Не получилось снять. Попробуй ещё раз."))
                      .finally(() => setBusy(false));
                  }}
                  style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-3)",
                           padding: 14, borderRadius: "var(--r-field)", border: "none",
                           cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                           fontWeight: 600, background: "var(--surface-2)", color: "var(--text)" }}>
                  Вернуть биржевой для {rate.quote}
                </button>
              ))}
              {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function formatUpdated(iso: string | null): string {
  if (!iso) return "курс из запаса — сеть была недоступна";
  const when = new Date(iso);
  // Словами, как везде: числовая дата тут была единственной на всё приложение.
  return `обновлён ${dayLabel(when.toISOString().slice(0, 10))}`;
}

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю курсы">
      <div style={{ height: 11, width: "50%", background: "var(--surface-3)", borderRadius: 5 }} />
    </div>
  );
}

